import { colors, ColorType } from '@styles/colors'
import { typography } from '@styles/typography'
import { SharedProps } from 'baseui/input'
import { borderColor, padding } from 'polished'
import { PropsWithChildren, useCallback } from 'react'
import { InputSize } from './input.interface'

export type InputStyleArgs = PropsWithChildren<SharedProps>

export const useInputStyle = () => {
  const {
    // input text
    gray700: textColor,
    gray300: disabledColor,
    // placeholder
    gray300: placeholderColor,
    gray200: disabledPlaceholderColor,
    // border
    gray50: inputBorderColor,
    gray50: hoverBorderColor,
    blue400: focusBorderColor,
    gray50: disabledBorderColor,
    green400: positiveBorderColor,
    red400: errorBorderColor,
    // background
    gray50: bgColor,
    gray50: hoverBgColor,
    white: focusBgColor,
    gray50: disabledBgColor,
    green50: positiveBgColor,
    red50: errorBgColor,
    // label
    gray400: labelColor,
    blue400: focusLabelColor,
    gray400: disabledLabelColor,
    green400: positiveLabelColor,
    red400: errorLabelColor,
    // action
    blue400: actionColor,
    gray400: disabledActionColor,
    green400: positiveActionColor,
    red400: errorActionColor,
    // icon
    gray500: iconColor,
    gray700: dirtyIconColor,
    blue400: focusIconColor,
    gray300: disabledIconColor,
    green400: positiveIconColor,
    red400: errorIconColor,
  } = colors

  const getBackgroundColor = useCallback(
    (
      { $disabled, $error, $positive, $isFocused }: InputStyleArgs,
      isHover?: boolean
    ) => {
      let color: ColorType = bgColor
      if (isHover) {
        color = hoverBgColor
      }
      if ($isFocused) {
        color = focusBgColor
      }
      if ($positive) {
        color = positiveBgColor
      }
      if ($error) {
        color = errorBgColor
      }
      if ($disabled) {
        color = disabledBgColor
      }
      return { backgroundColor: color }
    },
    [
      bgColor,
      disabledBgColor,
      errorBgColor,
      focusBgColor,
      hoverBgColor,
      positiveBgColor,
    ]
  )

  const getBorderColor = useCallback(
    (
      { $isFocused, $disabled, $error, $positive }: InputStyleArgs,
      isHover?: boolean
    ) => {
      let color: ColorType = inputBorderColor
      if (isHover) {
        color = hoverBorderColor
      }
      if ($isFocused) {
        color = focusBorderColor
      }
      if ($positive) {
        color = positiveBorderColor
      }
      if ($error) {
        color = errorBorderColor
      }
      if ($disabled) {
        color = disabledBorderColor
      }
      return { ...borderColor(color) }
    },
    [
      disabledBorderColor,
      errorBorderColor,
      focusBorderColor,
      hoverBorderColor,
      inputBorderColor,
      positiveBorderColor,
    ]
  )

  const getPlaceholderColor = useCallback(
    ({ $disabled }: InputStyleArgs) => {
      let color: ColorType = placeholderColor
      if ($disabled) {
        color = disabledPlaceholderColor
      }
      return {
        '::placeholder': {
          '-webkit-text-fill-color': color,
        },
      }
    },
    [disabledPlaceholderColor, placeholderColor]
  )

  const getLabelColor = useCallback(
    ({ $isFocused, $disabled, $error, $positive }: InputStyleArgs) => {
      let color: ColorType = labelColor
      if ($isFocused) {
        color = focusLabelColor
      }
      if ($positive) {
        color = positiveLabelColor
      }
      if ($error) {
        color = errorLabelColor
      }
      if ($disabled) {
        color = disabledLabelColor
      }
      return { color }
    },
    [
      disabledLabelColor,
      errorLabelColor,
      focusLabelColor,
      labelColor,
      positiveLabelColor,
    ]
  )

  const getActionColor = useCallback(
    ({ $disabled, $error, $positive }: InputStyleArgs) => {
      let color: ColorType = actionColor
      if ($positive) {
        color = positiveActionColor
      }
      if ($error) {
        color = errorActionColor
      }
      if ($disabled) {
        color = disabledActionColor
      }
      return { color }
    },
    [actionColor, disabledActionColor, errorActionColor, positiveActionColor]
  )

  const getIconColor = (
    { $disabled, $isFocused, $error, $positive }: InputStyleArgs,
    isDirty?: boolean
  ) => {
    let color: ColorType = iconColor
    if (isDirty) {
      color = dirtyIconColor
    }
    if ($isFocused) {
      color = focusIconColor
    }
    if ($positive) {
      color = positiveIconColor
    }
    if ($error) {
      color = errorIconColor
    }
    if ($disabled) {
      color = disabledIconColor
    }
    return color
  }

  const getSize = ($size: InputSize) =>
    ({
      l: {
        ...typography.LabelL,
        ...padding('14px'),
      },
      m: {
        ...typography.LabelL,
        ...padding('10px', '14px'),
      },
      s: {
        ...typography.LabelM,
        ...padding('6px', '14px'),
      },
    }[$size])

  const getWithLabelSize = ($size: InputSize) =>
    ({
      l: {
        ...typography.LabelL,
        ...padding('22px', '14px', '6px'),
      },
      m: {
        ...typography.LabelL,
        ...padding('18px', '14px', '2px'),
      },
      s: {
        ...typography.LabelM,
        ...padding('18px', '14px', '2px'),
      },
    }[$size])

  const getWithLabelActionPostioin = ($size: InputSize) =>
    ({
      l: {
        top: '22px',
      },
      m: {
        top: '18px',
      },
      s: {
        top: '18px',
      },
    }[$size])

  const getSelectSize = ($size: InputSize, hasLabel?: boolean) =>
    ({
      l: {
        ...typography.LabelL,
        paddingTop: hasLabel ? '22px' : '14px',
        paddingBottom: hasLabel ? '6px' : '14px',
      },
      m: {
        ...typography.LabelL,
        paddingTop: hasLabel ? '18px' : '10px',
        paddingBottom: hasLabel ? '2px' : '10px',
      },
      s: {
        ...typography.LabelM,
        paddingTop: hasLabel ? '18px' : '6px',
        paddingBottom: hasLabel ? '2px' : '6px',
      },
    }[$size])

  return {
    backgroundColor: bgColor,
    color: textColor,
    placeholderColor,
    disabledColor,
    getBackgroundColor,
    getBorderColor,
    getLabelColor,
    getActionColor,
    getSize,
    getWithLabelSize,
    getPlaceholderColor,
    getIconColor,
    getWithLabelActionPostioin,
    getSelectSize,
  }
}
